import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Paper,
    Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { DeleteLearnerNotes, getLearnerNotes } from '../../../../../services/mentorServices/ReviewServices';
import { toast } from 'react-toastify';
import AddNoteModal from './AddNoteModal';

function DisplayNotesModal({ open, onClose, selectedNotesTopic,fetchReviewTopics }) {
    const [notes, setNotes] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [noteToDelete, setNoteToDelete] = useState(null);

    const formatDateTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    };

    const fetchNotes = async () => {
        try {
            const response = await getLearnerNotes(
                selectedNotesTopic?.learner_id,
                selectedNotesTopic?.makerplanID,
                selectedNotesTopic?.moduleID,
                selectedNotesTopic?.topicID
            );

            if (Array.isArray(response?.data?.data)) {
                const sortedNotes = response.data.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setNotes(sortedNotes);
            } else {
                setNotes([]);
            }
        } catch (error) {
            toast.error('Failed to load notes.');
        }
    };

    useEffect(() => {
        if (open) {
            fetchNotes();
        }
    }, [open, selectedNotesTopic]);

    const handleEditClick = (note) => {
        setSelectedNote(note);
        setModalOpen(true);
    };

    const handleAddClick = () => {
        setSelectedNote(null);
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedNote(null);
        fetchNotes();
    };

    const confirmDelete = (note) => {
        setNoteToDelete(note);
        setConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            const response = await DeleteLearnerNotes(noteToDelete.id);
            const message = response?.data?.message || 'Note deleted successfully.';
            toast.success(message);
            setConfirmOpen(false);
            setNoteToDelete(null);
            fetchNotes();
            fetchReviewTopics()
        } catch (error) {
            const errMsg =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to delete note.';
            toast.error(errMsg);
            setConfirmOpen(false);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.25rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    📝 Notes for - {selectedNotesTopic?.topic_Name}
                    <Tooltip title="Add Note">
                        <IconButton onClick={handleAddClick}>
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                </DialogTitle>

                <DialogContent dividers>
                    <Box mt={1}>
                        {notes?.map((note, index) => (
                            <Paper
                                key={index}
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    backgroundColor: '#f9f9f9',
                                    borderRadius: 2,
                                    position: 'relative',
                                }}
                                elevation={2}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        display: 'flex',
                                        gap: 1,
                                    }}
                                >
                                    <Tooltip title="Edit Note">
                                        <IconButton size="small" onClick={() => handleEditClick(note)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete Note">
                                        <IconButton size="small" onClick={() => confirmDelete(note)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                <Box sx={{ pr: 6 }}>
                                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                        {note.track_note}
                                    </Typography>
                                </Box>

                                <Typography
                                    variant="caption"
                                    sx={{
                                        mt: 2,
                                        color: 'gray',
                                        textAlign: 'right',
                                        display: 'block',
                                    }}
                                >
                                    {formatDateTime(note.createdAt)}
                                </Typography>
                            </Paper>
                        ))}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} variant="outlined" color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {modalOpen && (
                <AddNoteModal
                    open={modalOpen}
                    onClose={handleModalClose}
                    selectedNotesTopic={selectedNotesTopic}
                    existingNote={selectedNote}
                    fetchReviewTopics={fetchReviewTopics}
                />
            )}

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this note? This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} color="primary" variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        Yes, Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default DisplayNotesModal;
